import React from "react";
import {
  Box,
  Typography,
  Paper,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  startOfWeek,
  addDays,
  isSameDay as fnsIsSameDay,
  format,
  getISOWeek
} from 'date-fns';
import { sv } from 'date-fns/locale';
import { CalendarItem, eventOccursOnDay } from "./calendarModel";
import { useDraggableEvent } from "./DraggableEvent";
import TimeDropZone, { TimeSlot } from "./TimeDropZone";

const getWeekDays = (date: Date): Date[] => {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};

const formatShortWeekDay = (date: Date): string => {
  return format(date, 'EEE', { locale: sv });
};

const formatShortDate = (date: Date): string => {
  return format(date, 'd', { locale: sv });
};

interface WeekViewProps {
  viewDate: Date;
  events: CalendarItem[];
  onEventClick: (event: React.MouseEvent<HTMLElement>, calendarItem: CalendarItem) => void;
  onEventHover?: (event: React.MouseEvent<HTMLElement>, calendarItem: CalendarItem) => void;
  onEventLeave?: () => void;
  renderEventIcon?: (event: CalendarItem) => React.ReactNode;
  showFullDay?: boolean;
}

interface EventWithOverlap extends CalendarItem {
  overlapCount: number;
  overlapIndex: number;
}

const findOverlappingEvents = (events: CalendarItem[]): EventWithOverlap[] => {
  return events.map((event, index) => {
    const overlaps = events.filter((otherEvent, otherIndex) => {
      if (index === otherIndex) return false;
      const eventStart = event.start.getTime();
      const eventEnd = event.end.getTime();
      const otherStart = otherEvent.start.getTime();
      const otherEnd = otherEvent.end.getTime();
      return eventStart < otherEnd && eventEnd > otherStart;
    });

    return {
      ...event,
      overlapCount: overlaps.length,
      overlapIndex: overlaps.filter((_, i) => i < index).length
    };
  });
};

// Extracted component for draggable week events
const DraggableWeekEvent: React.FC<{
  event: EventWithOverlap;
  startHour: number;
  endHour: number;
  hourHeight: number;
  topOffset: number;
  onEventClick: WeekViewProps["onEventClick"];
  onEventHover?: WeekViewProps["onEventHover"];
  onEventLeave?: WeekViewProps["onEventLeave"];
  renderEventIcon?: WeekViewProps["renderEventIcon"];
}> = ({ event, startHour, endHour, hourHeight, topOffset, onEventClick, onEventHover, onEventLeave, renderEventIcon }) => {
  const { setNodeRef, listeners, attributes, dragStyle } = useDraggableEvent(event);

  const eventStartHour = event.start.getHours() + event.start.getMinutes() / 60;
  const eventEndHour = event.end.getHours() + event.end.getMinutes() / 60;

  const clippedStartHour = Math.max(eventStartHour, startHour);
  const clippedEndHour = Math.min(eventEndHour, endHour);

  if (clippedStartHour >= endHour || clippedEndHour <= startHour) return null;

  const duration = clippedEndHour - clippedStartHour;
  const topPosition = (clippedStartHour - startHour) * hourHeight + 40 + topOffset;

  const startsEarlier = eventStartHour < startHour;
  const endsLater = eventEndHour > endHour;
  const hasOverlap = event.overlapCount > 0;

  const totalColumns = hasOverlap ? event.overlapCount + 1 : 1;
  const columnIndex = hasOverlap ? event.overlapIndex : 0;
  const width = hasOverlap ? `${90 / totalColumns}%` : "calc(100% - 8px)";
  const left = hasOverlap ? `${4 + (columnIndex * (90 / totalColumns))}%` : "4px";

  return (
    <Paper
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      elevation={hasOverlap ? 3 : 2}
      sx={{
        position: "absolute",
        left,
        width,
        top: `${topPosition}px`,
        height: `${duration * hourHeight}px`,
        minHeight: "20px",
        bgcolor: event.color || "primary.light",
        color: "white",
        overflow: "hidden",
        p: hasOverlap ? 0.3 : 0.5,
        cursor: "grab",
        display: "flex",
        flexDirection: "column",
        border: (startsEarlier || endsLater) ? "2px dashed rgba(255,255,255,0.7)" :
                hasOverlap ? "1px solid rgba(255,255,255,0.5)" : "none",
        borderRadius: 1,
        zIndex: hasOverlap ? 5 + columnIndex : 1,
        '&:hover': {
          bgcolor: event.color ? `${event.color}dd` : "primary.main",
          transform: "scale(1.02)",
          zIndex: 20,
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
        },
        transition: "all 0.2s ease",
        ...dragStyle,
      }}
      onClick={(e) => onEventClick(e, event)}
      onMouseEnter={onEventHover ? (e) => onEventHover(e, event) : undefined}
      onMouseLeave={onEventLeave}
    >
      {hasOverlap && (
        <Box sx={{
          position: "absolute",
          top: 2,
          right: 2,
          width: 16,
          height: 16,
          borderRadius: "50%",
          bgcolor: "warning.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "10px",
          fontWeight: "bold"
        }}>
          {totalColumns}
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {startsEarlier && (
          <Typography variant="caption" sx={{ fontSize: "8px" }}>↑</Typography>
        )}
        {renderEventIcon && !hasOverlap && (
          <Box sx={{ flexShrink: 0 }}>
            {renderEventIcon(event)}
          </Box>
        )}
        <Typography
          variant="caption"
          fontWeight="bold"
          noWrap
          sx={{ fontSize: hasOverlap ? "10px" : "11px" }}
        >
          {hasOverlap && event.title.length > 12
            ? `${event.title.substring(0, 10)}...`
            : event.title
          }
        </Typography>
        {endsLater && (
          <Typography variant="caption" sx={{ fontSize: "8px" }}>↓</Typography>
        )}
      </Box>

      {duration > 0.75 && !hasOverlap && (
        <Typography variant="caption" sx={{ fontSize: "9px", opacity: 0.9 }}>
          {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')}
        </Typography>
      )}

      {hasOverlap && duration > 0.5 && (
        <Typography variant="caption" sx={{ fontSize: "8px", opacity: 0.8 }}>
          {format(event.start, 'HH:mm')}
        </Typography>
      )}

      {duration > 1 && event.location && !hasOverlap && (
        <Typography variant="caption" noWrap sx={{ fontSize: "10px", mt: 0.5, opacity: 0.8 }}>
          📍 {event.location}
        </Typography>
      )}
    </Paper>
  );
};

const WeekView: React.FC<WeekViewProps> = ({
  viewDate,
  events,
  onEventClick,
  onEventHover,
  onEventLeave,
  renderEventIcon,
  showFullDay = false
}) => {
  const getEventsForDay = (day: Date): CalendarItem[] => {
    return events.filter(event => eventOccursOnDay(event, day));
  };

  const weekDays = getWeekDays(viewDate);
  const startHour = showFullDay ? 0 : 6;
  const endHour = showFullDay ? 24 : 18;
  const workingHours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const hourHeight = showFullDay ? 50 : 80;
  const slotHeight = hourHeight / 2;

  // Heldagsrad: samma höjd i alla kolumner så att timetiketterna hålls i linje
  const ALL_DAY_ROW_HEIGHT = 22;
  const allDayEventsByDay = weekDays.map(day =>
    events.filter(event => event.isAllDay && eventOccursOnDay(event, day))
  );
  const maxAllDayCount = Math.max(0, ...allDayEventsByDay.map(list => list.length));
  const allDayLaneHeight = maxAllDayCount * ALL_DAY_ROW_HEIGHT + (maxAllDayCount > 0 ? 4 : 0);

  return (
    <Box sx={{ height: "calc(100vh - 200px)", overflow: "auto" }}>
      <Grid container>
        <Grid size={1}>
          <Box
            sx={{
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ color: "text.secondary" }}
            >
              v.{getISOWeek(viewDate)}
            </Typography>
          </Box>
          {allDayLaneHeight > 0 && (
            <Box
              sx={{
                height: `${allDayLaneHeight}px`,
                borderBottom: "2px solid #e0e0e0",
                pr: 1,
                textAlign: "right",
              }}
            >
              <Typography variant="caption" sx={{ fontSize: "10px", color: "text.secondary" }}>
                Heldag
              </Typography>
            </Box>
          )}
          {workingHours.map(hour => (
            <Box
              key={hour}
              sx={{
                height: `${hourHeight}px`,
                borderBottom: "1px solid #e0e0e0",
                pr: 1,
                textAlign: "right",
                display: "flex",
                alignItems: "flex-start",
                pt: 1
              }}
            >
              <Typography variant="body2" sx={{ fontSize: "12px", color: "text.secondary" }}>
                {`${hour.toString().padStart(2, '0')}:00`}
              </Typography>
            </Box>
          ))}
        </Grid>

        {weekDays.map((day, dayIndex) => {
          const eventsForDay = getEventsForDay(day);
          const allDayEvents = allDayEventsByDay[dayIndex];
          const timedEvents = eventsForDay.filter(event => !event.isAllDay);
          const eventsWithOverlaps = findOverlappingEvents(timedEvents);
          const isToday = fnsIsSameDay(day, new Date());
          const dateStr = format(day, 'yyyy-MM-dd');

          const daySlots: TimeSlot[] = [];
          for (const hour of workingHours) {
            for (const minute of [0, 30]) {
              daySlots.push({
                id: `week-${dateStr}-${hour}-${minute}`,
                date: day,
                hour,
                minute,
              });
            }
          }

          return (
            <Grid key={day.toString()} size={1.57} sx={{ position: "relative" }}>
              <Box
                sx={{
                  height: "40px",
                  textAlign: "center",
                  borderBottom: "1px solid #e0e0e0",
                  bgcolor: isToday ? "primary.light" : "background.paper",
                  color: isToday ? "white" : "inherit",
                  fontWeight: isToday ? "bold" : "normal",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Typography variant="body2">
                  {formatShortWeekDay(day)} {formatShortDate(day)}
                </Typography>
              </Box>

              {/* Heldagsrad */}
              {allDayLaneHeight > 0 && (
                <Box
                  sx={{
                    height: `${allDayLaneHeight}px`,
                    borderBottom: "2px solid #e0e0e0",
                    borderLeft: "1px solid #e0e0e0",
                    px: 0.25,
                    pt: "2px",
                    overflow: "hidden",
                  }}
                >
                  {allDayEvents.map(event => (
                    <Box
                      key={event.id}
                      sx={{
                        height: `${ALL_DAY_ROW_HEIGHT - 2}px`,
                        mb: "2px",
                        px: 0.5,
                        borderRadius: 1,
                        bgcolor: event.color || "primary.light",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                        cursor: "pointer",
                        '&:hover': {
                          bgcolor: event.color ? `${event.color}dd` : "primary.main",
                        },
                      }}
                      onClick={(e) => onEventClick(e, event)}
                      onMouseEnter={onEventHover ? (e) => onEventHover(e, event) : undefined}
                      onMouseLeave={onEventLeave}
                    >
                      <Typography variant="caption" fontWeight="bold" noWrap sx={{ fontSize: "10px" }}>
                        {event.title}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Drop zone slots */}
              {daySlots.map((slot) => (
                <TimeDropZone key={slot.id} slot={slot} height={slotHeight}>
                  <Box
                    sx={{
                      height: "100%",
                      borderBottom: slot.minute === 0 ? "1px solid #e0e0e0" : "1px dashed #f0f0f0",
                      borderLeft: "1px solid #e0e0e0",
                      bgcolor: slot.minute === 0 && slot.hour % 2 === 0 ? "grey.50" : "background.paper",
                    }}
                  />
                </TimeDropZone>
              ))}

              {/* Draggable events */}
              {eventsWithOverlaps.map(event => (
                <DraggableWeekEvent
                  key={event.id}
                  event={event}
                  startHour={startHour}
                  endHour={endHour}
                  hourHeight={hourHeight}
                  topOffset={allDayLaneHeight}
                  onEventClick={onEventClick}
                  onEventHover={onEventHover}
                  onEventLeave={onEventLeave}
                  renderEventIcon={renderEventIcon}
                />
              ))}
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default WeekView;
